/**
 * Logger Utility
 * Centralized logging with environment-aware behavior
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

class Logger {
  /**
   * Debug level logging - only in development
   */
  debug(message: string, ...args: any[]) {
    if (isDevelopment) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Info level logging - only in development
   */
  info(message: string, ...args: any[]) {
    if (isDevelopment) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Warning level logging - all environments
   */
  warn(message: string, ...args: any[]) {
    console.warn(`[WARN] ${message}`, ...args);
  }

  /**
   * Error level logging - all environments
   */
  error(message: string, error?: any, ...args: any[]) {
    console.error(`[ERROR] ${message}`, error, ...args);
    
    // In production, you might want to send errors to a monitoring service
    if (isProduction && error) {
      // TODO: Integrate with error monitoring service (e.g., Sentry, LogRocket)
      // errorMonitoringService.captureException(error, { message, ...args });
    }
  }

  /**
   * API request logging - only in development
   */
  apiRequest(method: string, url: string, data?: any) {
    if (isDevelopment) {
      console.log(`[API REQUEST] ${method.toUpperCase()} ${url}`, data ? data : '');
    }
  }

  /**
   * API response logging - only in development
   */
  apiResponse(method: string, url: string, status: number, data?: any) {
    if (isDevelopment) {
      console.log(
        `[API RESPONSE] ${method.toUpperCase()} ${url} - ${status}`,
        data ? data : ''
      );
    }
  }

  /**
   * API error logging - all environments
   */
  apiError(method: string, url: string, error: any) {
    const status = error.response?.status || 'Network Error';
    const message = error.response?.data?.message || error.message;
    
    console.error(
      `[API ERROR] ${method.toUpperCase()} ${url} - ${status}`,
      message,
      error
    );

    if (isProduction) {
      // TODO: Send to monitoring service
    }
  }

  /**
   * Form validation error logging - only in development
   */
  validationError(formName: string, errors: any) {
    if (isDevelopment) {
      console.warn(`[VALIDATION ERROR] ${formName}`, errors);
    }
  }

  /**
   * User action logging - only in development
   */
  userAction(action: string, details?: any) {
    if (isDevelopment) {
      console.log(`[USER ACTION] ${action}`, details ? details : '');
    }
  }
}

export const logger = new Logger();
